import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { List } from "react-virtualized";
import { useSwitch } from "shared/dynamic-imports";
import { Button, FlexColumnGap } from "shared/ui-basics";
import { v4 as uuid } from "uuid";
import { CustomMenu, CustomTypeahead, EmptyListLabel, ListContainer, MenuBody, MenuItem, MenuLabel, TypeaheadContainer, AIButtonWrapper } from "./typeahead-styles";
import AISuggestion from "features/ai/components/AISuggestion";
import { useSelector } from "react-redux";

// renderSelectedItem: compulsory - (selectedItem) => ...
// renderMenuItem: compulsory if no renderCustomMenu props - ({ item, isSelected }) => ...
// linkMenuItem: compulsory if no renderCustomMenu props - ({ e, item }) => ...
// filter: compulsory - ({ query, option }) => ...
// renderCreateItem: optional, render function used when creating a new item - ({ input: text, clearInput, close}) => ...
// renderCustomMenu: optional, render the full menu (if we need a menu with categories for example) - ({ results, input: text, clearInput }) => ...

const GenericTypeahead = ({
	selected,
	options,
	renderSelectedItem,
	renderCustomMenu,
	renderMenuItem,
	renderCreateItem,
	linkMenuItem,
	filter,
	placeholder = "SEARCH_GENERIC",
	menuLabel = "",
	idAttribute = "id",
	labelKey = "name",
	menuListHeight = 268,
	globalWidth = "100%",
	isStrictOpen = undefined,
	id = uuid(),
	virtualScrollerParams = null,
	RenderFooter,
	showShadow = false,
	aiParams = undefined
}) => {
	const { t } = useTranslation();
	const typeaheadRef = useRef(null);
	const containerRef = useRef(null);
	const { open, close } = useSwitch();

	const [width, setWidth] = useState(0);

	const isCreateMode = renderCreateItem ? true : false;
	const aiAllowed = useSelector(({ settings }) => settings.subscriptionDetails.aiEnabled);

	useEffect(() => {
		if (containerRef.current) {
			setWidth(containerRef.current.offsetWidth);
		}
	}, [open, close]);

	const clearInput = () => {
		if (typeaheadRef.current) {
			typeaheadRef?.current?.setState({ text: "" });
		}
	};

	const onSelectMenuItem = ({ e, item }) => {
		clearInput();
		linkMenuItem({ e, item, close });
	};

	const getIsTextAlreadyExist = ({ options, text }) => {
		if (!text) return true;
		return options?.some((option) => text && option?.name?.toLowerCase() === text?.toLowerCase());
	};

	const renderMenu = (results, menuProps, state) => {
		const { text } = state;
		const selectedIds = selected?.map((s) => s?.[idAttribute]);
		const selectableItems = results?.filter((r) => !selectedIds?.includes(r?.[idAttribute]));
		const isTextAlreadyExist = getIsTextAlreadyExist({ options, text });
		const showEmptyList = results?.length === 0 && (!isCreateMode || (isCreateMode && isTextAlreadyExist));

		const renderListComponent = () => {
			if (renderCustomMenu) return renderCustomMenu({ results, input: text, clearInput, close });
			if (selectableItems?.length !== 0) {
				return (
					<>
						{menuLabel && <MenuLabel>{t(menuLabel)}</MenuLabel>}
						<ListContainer height={menuListHeight} isVirtualScroll={virtualScrollerParams}>
							<FlexColumnGap gap="8">
								{!virtualScrollerParams ? (
									selectableItems.map((item, idx) => {
										return (
											<div key={idx}>
												<MenuItem
													key={idx}
													id={item?.[idAttribute]}
													onClick={(e) => {
														e.preventDefault();
														onSelectMenuItem({ e, item });
													}}>
													{renderMenuItem({ item })}
												</MenuItem>
											</div>
										);
									})
								) : (
									<List
										width={width - 10}
										height={menuListHeight}
										rowCount={selectableItems?.length}
										rowHeight={virtualScrollerParams?.rowHeight}
										rowRenderer={({ index, style }) => (
											<div key={selectableItems[index].id} style={style} id={`searchPickList${selectableItems[index].id}`}>
												{renderMenuItem({ item: selectableItems[index] })}
											</div>
										)}
									/>
								)}
							</FlexColumnGap>
						</ListContainer>
					</>
				);
			}
			return <></>;
		};

		const renderCreateItemComponent = () => {
			if (isCreateMode && !isTextAlreadyExist) {
				return (
					<ListContainer>
						{renderCreateItem({ input: text, clearInput, close })}
					</ListContainer>
				);
			}
			return <></>;
		};

		return (
			<CustomMenu id={`typeahead_menu_${id}`}>
				{aiParams !== undefined && aiAllowed && <AIButtonWrapper>
					<Button  size='s' variant='primary-link' onClick={aiParams.openAI}>{t('ASK_ALEX_AI')}</Button>
					</AIButtonWrapper>}
				<MenuBody>

					{!showEmptyList ? (
						<>
							{renderCreateItemComponent()}
							{renderListComponent()}
						</>
					) : (
						<EmptyListLabel>{t("NO_RECORD_TO_DISPLAY")}</EmptyListLabel>
					)}
				</MenuBody>
				{RenderFooter && RenderFooter}
			</CustomMenu>
		);
	};

	const filterBy = (option, props) => filter && filter({ query: props.text, option });

	const handleMenuToggle = (isOpen) => {
		if (isOpen) return open();
		close()
	};

	return (
		<TypeaheadContainer ref={containerRef} width={globalWidth} showShadow={showShadow} showAI={aiParams?.show}>
			{aiParams?.show  && aiAllowed ? <AISuggestion {...aiParams?.ComponentParams}/> :

				<CustomTypeahead
					id={`typeahead_${id}`}
					ref={typeaheadRef}
					labelKey={labelKey}
					placeholder={t(placeholder)}
					multiple
					onMenuToggle={handleMenuToggle}
					options={options}
					onChange={() => { }}
					selected={selected}
					renderToken={renderSelectedItem}
					renderMenu={renderMenu}
					filterBy={filterBy}
					showborder={true}
					width="100%"
					open={isStrictOpen}
					showShadow={showShadow}
					autoFocus
				/>}
		</TypeaheadContainer>
	);
};

GenericTypeahead.displayName = "GenericTypeahead";

export default GenericTypeahead;
